import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidBuildingIcon from 'LuxKit/Icons/solid/SolidBuildingIcon'
import { SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'
import { TimelineNode, TimelineEndNode } from './CancellationPolicyTimelineComponents'
import { getFlexibleCancellationDateChangeAllowance } from './flexibleCancellationPolicyTimelineUtils'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

interface Props {
  /** Days before check-in/departure for credit-only and non-refundable thresholds (e.g. 21 or 45). */
  days: number
  /** Days before check-in/departure after which the 7-day change-of-mind refund no longer applies (14, or 10 for the 10-day policy). */
  changeOfMindDays: number
  /** Whether this is a charter bundle (flights + hotel). Affects terminology and shows charter-specific fees. */
  isCharter?: boolean
  /** Offer-configured date change allowance: 'Unlimited' or a numeric string like '2'. */
  numberOfDateChangesAllowed?: string
}

function FlexibleCancellationPolicyTimelineModalWithoutDates(props: Props) {
  const { days, changeOfMindDays, isCharter, numberOfDateChangesAllowed } = props
  const intl = useIntl()
  const dateChangeAllowance = useMemo(
    () => getFlexibleCancellationDateChangeAllowance(numberOfDateChangesAllowed, isCharter),
    [isCharter, numberOfDateChangesAllowed],
  )
  const fullRefundColour = useRebrandSuccessDarkColour()

  const checkInOrDepartureLabel = isCharter
    ? intl.formatMessage({
        id: 'dAYHFZkXhpQA6WMGvbmK',
        defaultMessage: 'Departure',
      })
    : intl.formatMessage({
        id: 'poCu5gwZHqlbN808pfIt',
        defaultMessage: 'Check-in',
      })

  const noRefundLabel = isCharter
    ? intl.formatMessage({
        id: 'A0vlpq05IK1xYwLmMHpH',
        defaultMessage: 'From {days} days until departure',
      }, { days })
    : intl.formatMessage({
        id: 'tl7_BtwbsRRz0pGZvRke',
        defaultMessage: 'Fewer than {days} days before check-in',
      }, { days })

  return (
    <>
      <Group direction="vertical">
        <TimelineNode
          label={intl.formatMessage({
            id: 'vjetCLF3dFVHyJnSwn31',
            defaultMessage: 'Within {days} days of purchase',
          }, { days: SEVEN_DAYS_AFTER_PURCHASE })}
        >
          <BodyText variant="medium" weight="bold" colour={fullRefundColour}>
            {intl.formatMessage({
              id: 'IPvnv3wz6i5O1pItlNwB',
              defaultMessage: 'Full refund',
            })}
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium" as="p">
              {intl.formatMessage({
                id: 'VyE5pEAHa1hOdfIjp2TD',
                defaultMessage: 'Cancel within {days} days of purchase to receive a full refund to your original payment method or credit.',
              }, { days: SEVEN_DAYS_AFTER_PURCHASE })}
            </BodyText>
            <div>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'd68PXUpxHYTTkOyV6HWI',
                  defaultMessage: 'Conditions apply:',
                })}
              </BodyText>
              <BodyText variant="medium">
                {isCharter && intl.formatMessage({
                  id: '5p9fFIBIsdjfCPNoamtK',
                  defaultMessage: 'The cancellation must be made at least {days} days before departure.',
                }, { days: changeOfMindDays })}
                {!isCharter && intl.formatMessage({
                  id: 'aD3y8uk6ZrNvsIQInDof',
                  defaultMessage: 'The cancellation must be made at least {days} days before check-in.',
                }, { days: changeOfMindDays })}
              </BodyText>
            </div>
          </Group>
        </TimelineNode>

        {!isCharter && (
          <TimelineNode
            label={intl.formatMessage({
              id: '9e42YUlMptEMf3Fo2_tE',
              defaultMessage: 'More than {days} days before check-in',
            }, { days })}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'BkK38vGr7Mr942F0vUUA',
                defaultMessage: 'Full refund to credit',
              })}
            </BodyText>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'ryBM_QsLa9xln8IqIZUw',
                defaultMessage: 'Cancel more than {days} days before check-in to receive a 100% refund to credit.',
              }, { days })}
            </BodyText>
          </TimelineNode>
        )}

        {isCharter && (
          <TimelineNode
            label={intl.formatMessage({
              id: 'k4zEXAAK16mZ04r4oxe9',
              defaultMessage: 'Up to {days} days prior to departure',
            }, { days: days + 1 })}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'KsmgOLy4pOps0C4ljO4T',
                defaultMessage: 'Partial refund',
              })}
            </BodyText>
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'Ke5udm-SsFdx4U4Ew4yz',
                  defaultMessage: 'If you cancel at least {days} days before departure, a cancellation fee will apply:',
                }, { days })}
              </BodyText>
              <FlexibleCancellationCharterFlightFees />
            </Group>
          </TimelineNode>
        )}

        <TimelineNode label={noRefundLabel}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'yAazKuRHWgNT7wkPi4c9',
              defaultMessage: 'No refund',
            })}
          </BodyText>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium">
              {isCharter && intl.formatMessage({
                id: '2Uf9qchxueheLUX4-6KY',
                defaultMessage: 'Cancellations made within {days} days of departure are non-refundable.',
              }, { days })}
              {!isCharter && intl.formatMessage({
                id: '1Y2aN6fen5SPgReqFeDM',
                defaultMessage: 'Cancellations made fewer than {days} days before check-in are non-refundable.',
              }, { days })}
            </BodyText>
            {changeOfMindDays < days && <BodyText variant="medium">
              {isCharter && intl.formatMessage({
                id: 'lO9Ctg1dfdT5jcXCWV_m',
                defaultMessage: 'However, you can still receive a full refund if you cancel within {purchaseDays} days of purchase and at least {beforeDepartureDays} days before departure.',
              }, {
                purchaseDays: SEVEN_DAYS_AFTER_PURCHASE,
                beforeDepartureDays: changeOfMindDays,
              })}
              {!isCharter && intl.formatMessage({
                id: 'w4J4qQ1yQ2Jobi2jVTBY',
                defaultMessage: 'You can still receive a full refund if you cancel within {purchaseDays} days of purchase and at least {beforeDepartureDays} days before check-in.',
              }, {
                purchaseDays: SEVEN_DAYS_AFTER_PURCHASE,
                beforeDepartureDays: changeOfMindDays,
              })}
            </BodyText>}
          </Group>
        </TimelineNode>

        <TimelineEndNode label={checkInOrDepartureLabel} icon={!isCharter ? SolidBuildingIcon : undefined} />
      </Group>

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'zOgZxgWqO_0Kba00Iaqk',
            defaultMessage: 'Date changes',
          })}
        </BodyText>
        <BodyText variant="medium">
          {dateChangeAllowance.kind === 'unlimited' && intl.formatMessage({
            id: 'qByNsBGZAw-Ycu4EFooM',
            defaultMessage: 'Unlimited changes permitted if made more than {days} days before check-in. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, { days })}
          {isCharter && intl.formatMessage({
            id: 'zfB933FaHQfL74MWwc7L',
            defaultMessage: 'One change permitted if made more than {days} days before departure. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, { days })}
          {dateChangeAllowance.kind === 'limited' && dateChangeAllowance.count === 1 && !isCharter && intl.formatMessage({
            id: '4NeNYPfYSbTZy4mxmC8m',
            defaultMessage: 'One change permitted if made more than {days} days before check-in. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, { days })}
          {dateChangeAllowance.kind === 'limited' && dateChangeAllowance.count > 1 && !isCharter && intl.formatMessage({
            id: 'r7TzVtS-p8l6mBzJ1WsP',
            defaultMessage: '{count, plural, one {One change} other {# changes}} permitted if made more than {days} days before check-in. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, { days, count: dateChangeAllowance.count })}
          {dateChangeAllowance.kind === 'none' && intl.formatMessage({
            id: 'q5IDmM7KYiN5jzfti-vk',
            defaultMessage: 'Date changes are not permitted for this offer.',
          })}
          {dateChangeAllowance.kind === 'unknown' && intl.formatMessage({
            id: 'fKQlbTFFJdsDgvLjjsjP',
            defaultMessage: 'Changes may be permitted if made more than {days} days before check-in, as per the offer terms and conditions. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, { days })}
        </BodyText>
      </div>
    </>
  )
}

export default FlexibleCancellationPolicyTimelineModalWithoutDates
